import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Alert, Button, Checkbox, Col, Radio, Row, Select, Space, Tag, Tooltip, message } from "antd";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import ImportadorExcelGenerico from "../../../../../Components/ImportadorExcelGenerico";
import { SelectItem } from "../../../../../Components/Items";
import ModalProgreso from "../../../../../Components/ModalProgreso";
import SeguidorProcesoMasivo from "../../../../../Components/seguirProcesoMasivo";
import ResultadosFichasPago from "./ResultadosFichasPago";
import fichasPagoPdf from "./fichasPagoPdf";
import { NOMBRE_PLANTILLA, construirPlantilla } from "./plantillaFichasPago";
import {
  MODOS,
  analizarArchivo,
  analizarArchivoLista,
  colorEstado,
  construirIndices,
  detectarColumnas,
  etiquetaEstado,
  lecturaDeFilas,
  llavesParaRevision,
} from "./analizarFichasPago";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

// La generación corre en el servidor (lanzarProcesoMasivo) con este nombre de operación.
const OPERACION = "generarFichasPago";

// Mismo tope que MAX_LLAVES_REVISION del backend.
const LLAVES_POR_CONSULTA = 5000;

const dinero = (valor) =>
  valor === null || valor === undefined || valor === ""
    ? ""
    : new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(valor) || 0);

const recortar = (textoLargo, maximo) =>
  textoLargo.length > maximo ? `${textoLargo.slice(0, maximo)}…` : textoLargo;

const partirEnBloques = (lista, tamano) => {
  const bloques = [];
  for (let i = 0; i < lista.length; i += tamano) bloques.push(lista.slice(i, i + tamano));
  return bloques;
};

const instrucciones = [
  <>
    Elige <b>qué se cobra</b> antes de subir el archivo. <b>Lo que diga el archivo</b>: un renglón por cargo con
    matrícula, <b>concepto</b> (la clave del insumo, por ejemplo COLEGIATURA) y <b>monto</b>; cada renglón es una
    ficha con su propia <b>referencia de pago</b>. <b>La lista escolar del alumno</b>: basta la matrícula; la ficha
    lleva lo que el alumno todavía no tiene de su lista de grupo y de su lista individual, igual que «Compra
    Directa» de la tienda.
  </>,
  <>
    El <b>monto</b> es el total a pagar <b>con impuestos</b>. Si lo dejas vacío se usa el precio de lista del
    alumno. La <b>fecha límite</b> sólo se imprime: la ficha no se cancela sola.
  </>,
  <>
    A cada alumno se le registra una <b>remisión pendiente de pago</b> con las cuentas bancarias de su escuela
    (la transferencia fija de Configuración eCommerce). El pago se aplica con <b>Ventas eCommerce → Conciliar
    transferencias por Excel</b>, Confirmar transferencia, Pagar manualmente o desde Cobranza.
  </>,
  <>
    Con el archivo, usa la columna <b>periodo</b> en los cargos que se repiten (NOV-2026): así no se le puede
    generar dos veces al mismo alumno el mismo concepto del mismo periodo. Con la lista, lo que ya tiene en una
    venta (pagada o con ficha pendiente) no se vuelve a cobrar. En los dos casos el archivo se puede volver a
    subir sin duplicar.
  </>,
  <>
    La generación <b>corre en el servidor</b>, una ficha tras otra, y puede tardar varios minutos. Puedes salir
    de la pantalla: sigue sola y al volver ves su avance. Al terminar se muestra el detalle fila por fila, el
    Excel de resultados y el <b>PDF con todas las fichas</b>.
  </>,
];

class GenerarFichasPago extends Component {
  state = {
    catalogos: null,
    // Qué se cobra (MODOS). `modoDelArchivo` es con el que se analizó el archivo cargado: si
    // el usuario cambia de modo después, hay que volver a subirlo.
    modo: "concepto",
    modoDelArchivo: null,
    proyectoId: undefined,
    formaDePagoId: undefined,
    bancoId: undefined,
    enviarCorreo: true,
    mostrarEnTienda: true,
    descargarPdfAlTerminar: false,
    resumen: null,
    modalVisible: false,
    resultado: null,
    generandoPdf: false,
    // Generación en curso en el servidor.
    corridaId: null,
    progresoVisible: false,
    progreso: null,
    deteniendo: false,
    iniciando: false,
    // La última corrida del usuario, para ofrecer sus resultados al volver a la pantalla.
    ultimaCorrida: null,
    verCorridaAnterior: false,
  };

  componentDidMount() {
    this.seguidor = new SeguidorProcesoMasivo({
      empresaId: this.props.empresaId,
      headersToken: this.props.headersToken,
      onProgreso: (progreso) => {
        if (progreso.operacion && progreso.operacion !== OPERACION) return;
        this.setState({ progreso });
      },
      onFin: () => this.alTerminarCorrida(),
      onError: (mensaje) => {
        message.error(mensaje || "La generación se interrumpió");
        this.alTerminarCorrida();
      },
    });
    this.seguidor.escuchar();
    this.revisarCorridaAnterior();
  }

  componentWillUnmount() {
    this.desmontado = true;
    if (this.seguidor) this.seguidor.detener();
  }

  /**
   * Al entrar: si el usuario tiene una generación corriendo (salió de la pantalla a media
   * corrida) se vuelve a pegar a ella; si ya terminó, se ofrecen sus resultados.
   */
  revisarCorridaAnterior = async () => {
    let corrida = null;
    try {
      const res = await axios.post(
        "fichasPagoAlumnos/estadoFichasPago",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken },
      );
      corrida = res.data.success === 2 ? res.data.corrida : null;
    } catch (err) {
      return;
    }

    if (!corrida || this.desmontado) return;

    if (!corrida.terminado) {
      const avance = await this.seguidor.consultarEnCurso();
      if (avance && avance.operacion === OPERACION) {
        this.setState({ corridaId: corrida.corridaId, progresoVisible: true, progreso: avance });
        return;
      }
      // Lo que esté corriendo es otra operación masiva (o la generación murió con el
      // servidor): no es nuestro modal. Se vuelve a escuchar en limpio.
      this.seguidor.detener();
      this.seguidor.escuchar();
    }

    this.setState({ ultimaCorrida: corrida });
  };

  /**
   * Arranca la generación en el servidor. La llama el importador genérico con el mismo
   * payload de siempre (prop `enviarImportacion`).
   */
  enviarImportacion = async (payload) => {
    if (this.state.iniciando || this.state.progresoVisible) return;
    this.setState({ iniciando: true });

    let res;
    try {
      res = await axios.post("fichasPagoAlumnos/iniciarFichasPago", payload, { headers: this.props.headersToken });
    } catch (err) {
      this.setState({ iniciando: false });
      message.error(err.response?.data?.message || "No se pudo iniciar la generación");
      return;
    }

    if (res.data.success !== 2) {
      this.setState({ iniciando: false });
      message.error(res.data.message || "No se pudo iniciar la generación");
      return;
    }

    this.setState({
      iniciando: false,
      corridaId: res.data.corridaId,
      progresoVisible: true,
      deteniendo: false,
      ultimaCorrida: null,
      progreso: { procesados: 0, total: res.data.total ?? null, detalle: null },
    });
    this.seguidor.seguir(res.data.procesoId);
  };

  alTerminarCorrida = async () => {
    const { corridaId } = this.state;
    this.setState({ progresoVisible: false, deteniendo: false, progreso: null });
    if (corridaId) await this.cargarResultado(corridaId, { esCorridaNueva: true });
  };

  cargarResultado = async (corridaId, { esCorridaNueva = false } = {}) => {
    try {
      const res = await axios.post(
        "fichasPagoAlumnos/resultadoFichasPago",
        { empresaId: this.props.empresaId, corridaId },
        { headers: this.props.headersToken },
      );
      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudieron leer los resultados");
        return;
      }
      if (this.desmontado) return;
      this.setState({ resultado: res.data, modalVisible: true, corridaId: null }, () => {
        if (esCorridaNueva && this.state.descargarPdfAlTerminar) this.descargarPdf();
      });
    } catch (err) {
      message.error("No se pudieron leer los resultados de la generación");
    }
  };

  descargarPdf = () => {
    const { resultado } = this.state;
    const fichas = (resultado?.resultados || []).filter((valueRes) => valueRes.estado === "creada");
    if (fichas.length === 0) {
      message.info("No se generó ninguna ficha");
      return;
    }

    this.setState({ generandoPdf: true });
    // Con cientos de fichas pdfmake tarda unos segundos: se suelta el hilo para que el botón
    // alcance a pintar su estado de carga antes de empezar.
    setTimeout(() => {
      try {
        pdfMake
          .createPdf(fichasPagoPdf({ fichas, escuelas: resultado.escuelas, empresa: resultado.empresa }))
          .download("fichasDePago.pdf", () => {
            if (!this.desmontado) this.setState({ generandoPdf: false });
          });
      } catch (err) {
        console.log(err);
        message.error("No se pudo generar el PDF de las fichas");
        this.setState({ generandoPdf: false });
      }
    }, 50);
  };

  detenerImportacion = async () => {
    const { corridaId } = this.state;
    if (!corridaId) return;
    this.setState({ deteniendo: true });
    try {
      const res = await axios.post(
        "fichasPagoAlumnos/detenerFichasPago",
        { empresaId: this.props.empresaId, corridaId },
        { headers: this.props.headersToken },
      );
      if (res.data.success === 2) {
        message.info("Se detendrá al terminar la ficha en curso");
      } else {
        this.setState({ deteniendo: false });
        message.warning(res.data.message || "No se pudo detener");
      }
    } catch (err) {
      this.setState({ deteniendo: false });
      message.error("No se pudo detener la generación");
    }
  };

  // Sale de la pantalla: la generación sigue en el servidor y al volver se retoma el avance.
  salirSinDetener = () => {
    message.info("La generación sigue en el servidor. Vuelve a esta pantalla para ver su avance y sus resultados.");
    this.setState({ progresoVisible: false });
    this.irAtras();
  };

  irAtras = () => {
    if (this.props.location?.state?.volverA) {
      this.props.history.replace(this.props.location.state.volverA);
    } else {
      this.props.history.goBack();
    }
  };

  // El importador guarda los catálogos en su propio estado; este hook es el único punto
  // donde también llegan aquí, que es donde se necesitan para los selects.
  prepararCatalogos = (resData) => {
    const transferencia = (resData.formasDePago || []).find((forma) => Number(forma.c_FormaPago) === 3);
    this.setState((estado) => ({
      catalogos: resData,
      formaDePagoId: estado.formaDePagoId || (transferencia ? String(transferencia._id) : undefined),
    }));
    return resData;
  };

  /**
   * Fichas activas que ya existen para las filas del archivo. POSICIONAL respecto a las
   * lecturas (null donde no hay alumno y concepto resueltos).
   *
   * @returns {Promise<Array|null>} null si la consulta falló
   */
  revisarExistentes = async (lecturas) => {
    const llaves = llavesParaRevision(lecturas);
    const pendientes = [];
    llaves.forEach((llave, posicion) => {
      if (llave) pendientes.push({ posicion, llave });
    });

    const existentes = new Array(lecturas.length).fill(null);

    for (const bloque of partirEnBloques(pendientes, LLAVES_POR_CONSULTA)) {
      let res;
      try {
        res = await axios.post(
          "fichasPagoAlumnos/revisarFichasExistentes",
          { empresaId: this.props.empresaId, llaves: bloque.map((valuePendiente) => valuePendiente.llave) },
          { headers: this.props.headersToken },
        );
      } catch (err) {
        message.error("No se pudieron revisar las fichas que ya existen");
        return null;
      }

      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudieron revisar las fichas que ya existen");
        return null;
      }

      (res.data.resultados || []).forEach((valueResultado, indiceEnBloque) => {
        const pendiente = bloque[indiceEnBloque];
        if (pendiente) existentes[pendiente.posicion] = valueResultado;
      });
    }

    return existentes;
  };

  /**
   * Modo lista: lo que cada alumno del archivo ya tiene de sus listas, para mostrar sólo lo
   * pendiente. `{ [clienteId]: ["listaId|insumoId"] }`, o null si la consulta falló.
   */
  revisarCompradas = async (lecturas) => {
    const clienteIds = [
      ...new Set(lecturas.filter((lectura) => lectura.alumno).map((lectura) => String(lectura.alumno._id))),
    ];
    const compradas = {};

    for (const bloque of partirEnBloques(clienteIds, LLAVES_POR_CONSULTA)) {
      let res;
      try {
        res = await axios.post(
          "fichasPagoAlumnos/revisarListasCompradas",
          { empresaId: this.props.empresaId, clienteIds: bloque },
          { headers: this.props.headersToken },
        );
      } catch (err) {
        message.error("No se pudo revisar lo que ya compraron los alumnos");
        return null;
      }

      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudo revisar lo que ya compraron los alumnos");
        return null;
      }

      Object.assign(compradas, res.data.compradas || {});
    }

    return compradas;
  };

  /**
   * Pasada previa sobre el archivo completo, antes de validar fila por fila. Es `async` y el
   * importador genérico la espera: aquí se pregunta al servidor qué fichas ya existen (o, con
   * la lista, qué ya compró cada alumno), y es el único lugar desde donde se puede detectar una
   * fila repetida dentro del mismo archivo.
   */
  prepararContextoArchivo = async (hojas, catalogos) => {
    const modo = this.state.modo;
    const filas = hojas?.[0]?.data || [];
    const { columnas, faltantes, etiquetasFaltantes } = detectarColumnas(filas, modo);

    let bloqueado = null;
    if (faltantes.length > 0) {
      bloqueado = `No se encontraron las columnas obligatorias: ${etiquetasFaltantes}`;
      message.error(bloqueado);
    }

    const indices = construirIndices(catalogos);
    const lecturas = lecturaDeFilas({ filas, columnas, indices, modo });

    let decisiones;
    if (modo === "lista") {
      let compradas = {};
      if (!bloqueado) {
        compradas = await this.revisarCompradas(lecturas);
        if (!compradas) {
          bloqueado = "No se pudo revisar lo que ya compraron los alumnos; vuelve a subir el archivo";
          compradas = {};
        }
      }
      decisiones = analizarArchivoLista({ lecturas, compradas, indices, bloqueado });
    } else {
      let existentes = [];
      if (!bloqueado) {
        existentes = await this.revisarExistentes(lecturas);
        if (!existentes) {
          bloqueado = "No se pudieron revisar las fichas que ya existen; vuelve a subir el archivo";
          existentes = [];
        }
      }
      decisiones = analizarArchivo({ lecturas, existentes, indices, bloqueado });
    }

    const resumen = {};
    decisiones.forEach((valueDecision) => {
      const estado = valueDecision.datos?.estadoPrevisto || "error";
      resumen[estado] = (resumen[estado] || 0) + 1;
    });
    this.setState({ resumen, modoDelArchivo: modo });

    return { decisiones };
  };

  cambiarModo = (modo) => {
    if (this.state.modoDelArchivo && this.state.modoDelArchivo !== modo) {
      message.warning("Cambiaste qué se cobra: vuelve a subir el archivo para revisarlo con esta opción");
    }
    this.setState({ modo });
  };

  validarFila = (fila, catalogos, indice, contexto) =>
    contexto?.decisiones?.get(indice) || { ok: false, errores: [{ campo: "Fila", motivo: "No se pudo analizar" }] };

  columnasPreview = () => {
    const esLista = (this.state.modoDelArchivo || this.state.modo) === "lista";
    return [
    { title: "Fila", dataIndex: "numeroFila", key: "numeroFila", width: 60 },
    { title: "Matrícula", dataIndex: "matricula", key: "matricula", width: 110 },
    { title: "Alumno", dataIndex: "alumno", key: "alumno", width: 190 },
    { title: "Escuela", dataIndex: "escuela", key: "escuela", width: 150 },
    {
      title: esLista ? "Lista escolar" : "Concepto",
      key: "concepto",
      width: esLista ? 280 : 190,
      render: (_, record) => (
        <>
          <div>{record.conceptoNombre || record.concepto}</div>
          {esLista && record.detalleArticulos && (
            <Tooltip title={record.detalleArticulos}>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                {recortar(`${record.articulos} artículo(s) pendientes: ${record.detalleArticulos}`, 90)}
              </div>
            </Tooltip>
          )}
          {record.aviso && <div style={{ fontSize: 12, color: "#d48806" }}>{record.aviso}</div>}
        </>
      ),
    },
    ...(esLista ? [] : [{ title: "Periodo", dataIndex: "periodo", key: "periodo", width: 100 }]),
    {
      title: "Total a pagar",
      key: "totalEstimado",
      width: 140,
      align: "right",
      render: (_, record) =>
        record.totalEstimado === null || record.totalEstimado === undefined ? (
          ""
        ) : (
          <>
            <div>{dinero(record.totalEstimado)}</div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>{record.origenPrecio}</div>
          </>
        ),
    },
    {
      title: "Fecha límite",
      key: "fechaLimite",
      width: 110,
      render: (_, record) =>
        record.fechaLimite ? record.fechaLimite.split("-").reverse().join("/") : "",
    },
    {
      title: "Qué va a pasar",
      key: "estadoPrevisto",
      width: 140,
      render: (_, record) => <Tag color={colorEstado(record.estadoPrevisto)}>{etiquetaEstado(record.estadoPrevisto)}</Tag>,
    },
    {
      title: "Correo",
      key: "destinatarios",
      width: 220,
      render: (_, record) => record.destinatarios || <Tag color="orange">Sin correo</Tag>,
    },
    { title: "Motivo", dataIndex: "motivo", key: "motivo", width: 320 },
    ];
  };

  antesDeImportar = () => {
    const { catalogos, proyectoId, formaDePagoId, bancoId, modo, modoDelArchivo } = this.state;

    if (modoDelArchivo && modoDelArchivo !== modo) {
      message.warning("Cambiaste qué se cobra después de subir el archivo: vuelve a subirlo antes de generar");
      return false;
    }

    if (!proyectoId || !formaDePagoId) {
      message.warning("Selecciona el Proyecto y la Forma de Pago antes de generar las fichas");
      return false;
    }

    // Al confirmar el pago se usa el banco de la tienda en línea de la empresa o, si no hay,
    // el de la venta. Sin ninguno de los dos el pago no se podría aplicar.
    if (!catalogos?.tieneBancoOnline && !bancoId) {
      message.warning("Selecciona el banco donde se reciben los pagos de las fichas");
      return false;
    }

    return true;
  };

  prepararPayloadExtra = () => ({
    proyectoId: this.state.proyectoId,
    formaDePagoId: this.state.formaDePagoId,
    bancoId: this.state.bancoId || null,
    enviarCorreo: this.state.enviarCorreo === true,
    mostrarEnTienda: this.state.mostrarEnTienda === true,
    modo: this.state.modoDelArchivo || this.state.modo,
  });

  // Después de una generación se sale de la pantalla: la vista previa del archivo sigue
  // cargada y volver a darle Generar sólo daría "Ya tiene ficha", pero confunde. Si sólo se
  // estaban viendo los resultados de una corrida anterior, se queda en la pantalla.
  cerrarModal = () => {
    const eraCorridaAnterior = this.state.verCorridaAnterior;
    this.setState({ modalVisible: false, resultado: null, verCorridaAnterior: false });
    if (!eraCorridaAnterior) this.irAtras();
  };

  verResultadoCorridaAnterior = async () => {
    const { ultimaCorrida } = this.state;
    if (!ultimaCorrida) return;
    this.setState({ verCorridaAnterior: true });
    await this.cargarResultado(ultimaCorrida.corridaId);
  };

  renderCorridaAnterior = () => {
    const { ultimaCorrida } = this.state;
    if (!ultimaCorrida) return null;

    const cuando = ultimaCorrida.fin || ultimaCorrida.inicio;
    const fecha = cuando ? new Date(cuando).toLocaleString("es-MX") : "";
    const como =
      ultimaCorrida.interrumpida || !ultimaCorrida.terminado
        ? "se interrumpió antes de terminar"
        : ultimaCorrida.detenida
          ? "se detuvo antes de terminar"
          : "terminó";

    return (
      <Alert
        type={ultimaCorrida.terminado && !ultimaCorrida.interrumpida && !ultimaCorrida.detenida ? "success" : "warning"}
        showIcon
        style={{ marginBottom: 12 }}
        message={`Tu última generación de fichas (${fecha}) ${como}`}
        description="Puedes ver sus resultados y descargar el PDF de las fichas. Si quedó incompleta, vuelve a subir el mismo archivo: lo que ya quedó sale como «Ya tiene ficha»."
        action={
          <Button size="small" onClick={this.verResultadoCorridaAnterior}>
            Ver resultados
          </Button>
        }
      />
    );
  };

  renderResumen = () => {
    const { resumen } = this.state;
    if (!resumen) return null;

    const entradas = Object.entries(resumen).filter(([, cantidad]) => cantidad > 0);
    if (entradas.length === 0) return null;

    return (
      <div style={{ marginBottom: 12 }}>
        <Space size={[8, 8]} wrap>
          {entradas.map(([estado, cantidad]) => (
            <Tag color={colorEstado(estado)} key={estado}>
              {`${cantidad} · ${etiquetaEstado(estado)}`}
            </Tag>
          ))}
        </Space>
      </div>
    );
  };

  controlesExtra = () => {
    const {
      catalogos,
      modo,
      proyectoId,
      formaDePagoId,
      bancoId,
      enviarCorreo,
      mostrarEnTienda,
      descargarPdfAlTerminar,
    } = this.state;

    const escuelasSinConfigurar = (catalogos?.escuelas || []).filter((escuela) => escuela.problema);

    return (
      <>
        {this.renderCorridaAnterior()}

        {escuelasSinConfigurar.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${escuelasSinConfigurar.length} escuela(s) sin transferencia fija configurada`}
            description={`A sus alumnos no se les puede generar ficha hasta que se configuren sus cuentas bancarias en Configuración eCommerce → pago por escuela: ${escuelasSinConfigurar
              .map((escuela) => escuela.nombre)
              .join(", ")}.`}
          />
        )}

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>¿Qué se cobra en cada ficha?</div>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            value={modo}
            onChange={(e) => this.cambiarModo(e.target.value)}
            options={Object.entries(MODOS).map(([valor, opcion]) => ({ value: valor, label: opcion.etiqueta }))}
          />
          <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
            {modo === "lista"
              ? "Basta la columna MATRICULA. Cada alumno recibe una ficha con lo que todavía no tiene de su lista de grupo y su lista individual, al precio de su lista de precios. Concepto, monto y periodo del archivo se ignoran."
              : "Cada renglón del archivo (matrícula, concepto y monto) es una ficha con su propia referencia."}
          </div>
        </div>

        <Row gutter={[16, 8]} style={{ marginBottom: 12 }}>
          <Col xs={24} md={8}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Proyecto</div>
            <SelectItem
              style={{ width: "100%" }}
              placeholder="Proyecto"
              value={proyectoId}
              onChange={(valor) => this.setState({ proyectoId: valor })}
            >
              {(catalogos?.proyectos || []).map((proyecto) => (
                <Select.Option value={String(proyecto._id)} key={String(proyecto._id)}>
                  {proyecto.nombre}
                </Select.Option>
              ))}
            </SelectItem>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Forma de Pago</div>
            <SelectItem
              style={{ width: "100%" }}
              placeholder="Forma de Pago"
              value={formaDePagoId}
              onChange={(valor) => this.setState({ formaDePagoId: valor })}
            >
              {(catalogos?.formasDePago || []).map((forma) => (
                <Select.Option value={String(forma._id)} key={String(forma._id)}>
                  {forma.c_FormaPago + " - " + forma.descripcion}
                </Select.Option>
              ))}
            </SelectItem>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              Banco donde se reciben los pagos {catalogos?.tieneBancoOnline ? "(opcional)" : ""}
            </div>
            <SelectItem
              style={{ width: "100%" }}
              placeholder={catalogos?.tieneBancoOnline ? "El de la tienda en línea" : "Banco (MXN)"}
              value={bancoId}
              allowClear
              onChange={(valor) => this.setState({ bancoId: valor })}
            >
              {(catalogos?.bancos || []).map((banco) => (
                <Select.Option value={String(banco._id)} key={String(banco._id)}>
                  {banco.nombre}
                </Select.Option>
              ))}
            </SelectItem>
          </Col>
        </Row>

        <div style={{ marginBottom: 12 }}>
          <Space size={[24, 8]} wrap>
            <Checkbox checked={enviarCorreo} onChange={(e) => this.setState({ enviarCorreo: e.target.checked })}>
              Enviar la ficha por correo al alumno
            </Checkbox>
            <Tooltip title="La ficha aparece en «Mis pedidos» de la cuenta del alumno y en Ventas eCommerce, donde se puede confirmar el pago. Sin esta opción se cobra con la conciliación por Excel o desde Cobranza.">
              <Checkbox checked={mostrarEnTienda} onChange={(e) => this.setState({ mostrarEnTienda: e.target.checked })}>
                Mostrar en la tienda en línea
              </Checkbox>
            </Tooltip>
            <Checkbox
              checked={descargarPdfAlTerminar}
              onChange={(e) => this.setState({ descargarPdfAlTerminar: e.target.checked })}
            >
              Descargar el PDF de las fichas al terminar
            </Checkbox>
          </Space>
        </div>

        {catalogos && !catalogos.tieneBancoOnline && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="La empresa no tiene banco de tienda en línea configurado"
            description="Elige el banco donde se reciben los pagos: es el que se usa al aplicar el pago de cada ficha."
          />
        )}

        {this.renderResumen()}
      </>
    );
  };

  render() {
    return (
      <>
        <ImportadorExcelGenerico
          {...this.props}
          titulo="Generar Fichas de Pago"
          nombreEntidadPlural="fichas"
          endpointDatosImportar="fichasPagoAlumnos/datosParaFichasPago"
          endpointImportar="fichasPagoAlumnos/iniciarFichasPago"
          nombrePlantilla={NOMBRE_PLANTILLA}
          construirPlantilla={construirPlantilla}
          prepararCatalogos={this.prepararCatalogos}
          prepararContextoArchivo={this.prepararContextoArchivo}
          validarFila={this.validarFila}
          columnasPreview={this.columnasPreview()}
          instrucciones={instrucciones}
          controlesExtra={this.controlesExtra()}
          antesDeImportar={this.antesDeImportar}
          prepararPayloadExtra={this.prepararPayloadExtra}
          // La generación corre en el servidor: la pantalla manda el archivo una sola vez,
          // sigue el avance (ModalProgreso) y al final pide los resultados.
          enviarImportacion={this.enviarImportacion}
          etiquetasAccion={{ crear: "Generar", editar: null, sinCambios: "Ya tiene ficha", error: "No se puede" }}
          permitirEdicion
        />

        <ModalProgreso
          visible={this.state.progresoVisible}
          titulo="Generando fichas de pago"
          descripcion="Corre en el servidor, una ficha tras otra sin saturarlo. Puedes salir: sigue sola y al volver a esta pantalla ves su avance."
          procesados={this.state.progreso?.procesados || 0}
          total={this.state.progreso?.total ?? null}
          detalle={this.state.progreso?.detalle || null}
          unidad="fichas"
          onDetener={this.detenerImportacion}
          deteniendo={this.state.deteniendo}
          onOcultar={this.salirSinDetener}
        />

        <ResultadosFichasPago
          visible={this.state.modalVisible}
          resultado={this.state.resultado}
          cerrarModal={this.cerrarModal}
          descargarPdf={this.descargarPdf}
          generandoPdf={this.state.generandoPdf}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  empresaId: state.empresa.id,
  headersToken: state.user.headersToken,
});

export default connect(mapStateToProps)(GenerarFichasPago);
